import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { UserPlus, Search, Trash2, Users, ArrowLeft, CheckCircle, XCircle, AlertCircle, Loader2, Pencil } from "lucide-react";
import { getApiUrl } from "../../config/api";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  dateOfBirth?: string | null;
  age?: string | number | null;
  phone?: string;
  title?: string;
  nrcNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  jobTitle?: string;
  currentClassId?: string | number | null;
  className?: string | null;
  teacherClassId?: string | number | null;
  teacherClassName?: string | null;
  teacherAssignmentRole?: string | null;
  transportMode?: string;
  transportRouteId?: string | number | null;
  baseSalary?: string | number;
  housingAllowance?: string | number;
  transportAllowance?: string | number;
  responsibilityAllowance?: string | number;
  otherAllowances?: string | number;
}

interface TransportRouteOption {
  id: number;
  name: string;
  driverName?: string | null;
  driverContact?: string | null;
  fare?: number | null;
}

interface ClassOption {
  id: number;
  name: string;
}

const calculateAgeFromDate = (dateOfBirth?: string | null) => {
  if (!dateOfBirth) return null;
  const parsed = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDelta = today.getMonth() - parsed.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const AddUser: React.FC = () => {
  const blankFormData = {
    name: "",
    title: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    nrcNumber: "",
    bankName: "",
    bankAccountNumber: "",
    jobTitle: "",
    password: "",
    role: "student",
    classId: "",
    teacherClassId: "",
    teacherAssignmentRole: "class_teacher",
    transportMode: "personal",
    transportRouteId: "",
    baseSalary: "",
    housingAllowance: "0",
    transportAllowance: "0",
    responsibilityAllowance: "0",
    otherAllowances: "0"
  };
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [transportRoutes, setTransportRoutes] = useState<TransportRouteOption[]>([]);
  const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
  const [formData, setFormData] = useState(blankFormData);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const isEmployeeRole = ["teacher", "headteacher", "admin", "worker"].includes(formData.role);

  const token = localStorage.getItem("token");
  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("users/all"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.users || []);
      setFilteredUsers(res.data.users || []);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setMessage({ type: 'error', text: "Failed to load users" });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const fetchTransportRoutes = async () => {
      try {
        const res = await axios.get(getApiUrl("transport/routes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTransportRoutes(res.data?.routes || []);
      } catch (err) {
        setTransportRoutes([]);
      }
    };

    if (token) {
      fetchTransportRoutes();
    }
  }, [token]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAvailableClasses(res.data?.classes || []);
      } catch {
        setAvailableClasses([]);
      }
    };

    if (token) {
      fetchClasses();
    }
  }, [token]);
  // Filter users based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(
        users.filter(
          (user) =>
            user.name.toLowerCase().includes(term) ||
            user.email.toLowerCase().includes(term) ||
            user.role.toLowerCase().includes(term)
        )
      );
    }
    setCurrentPage(1); // reset to page 1 on every search change
  }, [searchTerm, users]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "role") {
      const nextRole = value;
      setFormData((prev) => ({
        ...prev,
        role: nextRole,
        classId: nextRole === "student" ? prev.classId : "",
        teacherClassId: nextRole === "teacher" ? prev.teacherClassId : "",
        teacherAssignmentRole: nextRole === "teacher" ? prev.teacherAssignmentRole : "class_teacher",
        transportMode: nextRole === "student" ? prev.transportMode : "personal",
        transportRouteId: nextRole === "student" ? prev.transportRouteId : ""
      }));
      return;
    }
    if (name === "transportRouteId") {
      setFormData((prev) => ({
        ...prev,
        transportRouteId: value,
        transportMode: value ? "school" : "personal"
      }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData(blankFormData);
    setEditingUser(null);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setDeleteConfirm(null);
    setFormData({
      name: user.name || "",
      title: user.title || "",
      dateOfBirth: user.dateOfBirth || "",
      email: user.email || "",
      phone: user.phone || "",
      nrcNumber: user.nrcNumber || "",
      bankName: user.bankName || "",
      bankAccountNumber: user.bankAccountNumber || "",
      jobTitle: user.jobTitle || "",
      password: "",
      role: normalizeRole(user.role) || "student",
      classId: user.currentClassId ? String(user.currentClassId) : "",
      teacherClassId: user.teacherClassId ? String(user.teacherClassId) : "",
      teacherAssignmentRole: user.teacherAssignmentRole || "class_teacher",
      transportMode: user.transportMode || "personal",
      transportRouteId: user.transportRouteId ? String(user.transportRouteId) : "",
      baseSalary: user.baseSalary?.toString?.() || "",
      housingAllowance: user.housingAllowance?.toString?.() || "0",
      transportAllowance: user.transportAllowance?.toString?.() || "0",
      responsibilityAllowance: user.responsibilityAllowance?.toString?.() || "0",
      otherAllowances: user.otherAllowances?.toString?.() || "0"
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      if (editingUser) {
        const res = await axios.put(
          getApiUrl(`users/${editingUser.id}`),
          {
            name: formData.name,
            title: formData.title,
            dateOfBirth: formData.role === "student" ? (formData.dateOfBirth || null) : null,
            email: formData.email,
            phone: formData.phone,
            nrcNumber: formData.nrcNumber,
            bankName: formData.bankName,
            bankAccountNumber: formData.bankAccountNumber,
            jobTitle: formData.jobTitle,
            password: formData.password.trim() || undefined,
            role: formData.role,
            transportMode: formData.transportMode,
            transportRouteId: formData.transportRouteId || null,
            currentClassId: formData.role === "student" ? (formData.classId || null) : null,
            teacherClassId: formData.role === "teacher" ? (formData.teacherClassId || null) : null,
            teacherAssignmentRole: formData.role === "teacher" ? formData.teacherAssignmentRole : null,
            baseSalary: formData.baseSalary,
            housingAllowance: formData.housingAllowance,
            transportAllowance: formData.transportAllowance,
            responsibilityAllowance: formData.responsibilityAllowance,
            otherAllowances: formData.otherAllowances
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: res.data.message || "User updated successfully!" });
      } else {
        const res = await axios.post(
          getApiUrl("auth/signup"),
          {
            ...formData,
            dateOfBirth: formData.role === "student" ? (formData.dateOfBirth || null) : null,
            currentClassId: formData.role === "student" ? (formData.classId || null) : null
            ,
            teacherClassId: formData.role === "teacher" ? (formData.teacherClassId || null) : null,
            teacherAssignmentRole: formData.role === "teacher" ? formData.teacherAssignmentRole : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: res.data.message || "User added successfully!" });
      }
      resetForm();
      await fetchUsers();
      setTimeout(() => setMessage(null), 5000);
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || (editingUser ? "Error updating user. Please try again." : "Error adding user. Please try again.")
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await axios.delete(getApiUrl(`users/${userId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: 'success', text: "User deleted successfully!" });
      if (editingUser?.id === userId) {
        resetForm();
      }
      fetchUsers();
      setDeleteConfirm(null);
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || "Error deleting user"
      });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const normalizeRole = (role?: string) => (role || "").trim().toLowerCase();

  const inferRoleKey = (userRole: string, hasHeadteacher: boolean) => {
    const normalized = normalizeRole(userRole);
    if (normalized) return normalized;
    return hasHeadteacher ? "unassigned" : "headteacher";
  };

  const formatRoleLabel = (roleKey: string) => {
    if (roleKey === "unassigned") return "Unassigned";
    return roleKey.charAt(0).toUpperCase() + roleKey.slice(1);
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      student: "bg-blue-100 text-blue-700",
      teacher: "bg-teal-100 text-teal-700",
      headteacher: "bg-purple-100 text-purple-700",
      admin: "bg-rose-100 text-rose-700",
      unassigned: "bg-gray-100 text-gray-700",
    };
    return colors[normalizeRole(role)] || "bg-gray-100 text-gray-700";
  };

  const calculatedStudentAge = formData.role === "student"
    ? calculateAgeFromDate(formData.dateOfBirth)
    : null;

  return (
    <div className="min-h-screen bg-slate-50/30 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/admin"
            className="p-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">User Management</h1>
            <p className="text-slate-500 mt-1">Add new users and manage existing accounts</p>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success'
            ? 'bg-green-50 border border-green-100'
            : 'bg-red-50 border border-red-100'
            }`}>
            {message.type === 'success' ? (
              <CheckCircle className="text-green-600" size={20} />
            ) : (
              <AlertCircle className="text-red-600" size={20} />
            )}
            <p className={`font-medium ${message.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
              {message.text}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Add User Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                  {editingUser ? <Pencil className="text-orange-600" size={20} /> : <UserPlus className="text-orange-600" size={20} />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{editingUser ? "Edit User" : "Add New User"}</h2>
                  <p className="text-sm text-slate-500">{editingUser ? "Update this account" : "Create a new account"}</p>
                </div>
              </div>

              {editingUser && (
                <div className="mb-5 flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-orange-800">Editing {editingUser.name}</p>
                    <p className="text-xs text-orange-700">Leave password blank to keep the current password.</p>
                  </div>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-orange-200 bg-white px-3 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                  >
                    Cancel
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmitUser} className="space-y-5">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                  />
                </div>

                {/* Role Select */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-2">
                    User Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 bg-white cursor-pointer"
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="headteacher">Headteacher</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-2">
                    {formData.role === "student" ? "Gender" : "Title"}
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    list="add-user-title-options"
                    placeholder={formData.role === "student" ? "Male or Female" : "Mr, Ms, Mrs, Miss..."}
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                  />
                  <datalist id="add-user-title-options">
                    {formData.role === "student" ? (
                      <>
                        <option value="Male" />
                        <option value="Female" />
                      </>
                    ) : (
                      <>
                        <option value="Mr" />
                        <option value="Ms" />
                        <option value="Mrs" />
                        <option value="Miss" />
                        <option value="Dr" />
                        <option value="Prof" />
                        <option value="Rev" />
                      </>
                    )}
                  </datalist>
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                  />
                </div>

                {/* Phone Field */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="e.g. +260971234567"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                  />
                </div>

                {isEmployeeRole && (
                  <>
                    <div>
                      <label htmlFor="jobTitle" className="block text-sm font-medium text-slate-700 mb-2">
                        Job Title
                      </label>
                      <input
                        type="text"
                        id="jobTitle"
                        name="jobTitle"
                        placeholder="e.g. Mathematics Teacher"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <label htmlFor="nrcNumber" className="block text-sm font-medium text-slate-700 mb-2">
                        NRC Number
                      </label>
                      <input
                        type="text"
                        id="nrcNumber"
                        name="nrcNumber"
                        placeholder="Enter NRC number"
                        value={formData.nrcNumber}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <label htmlFor="bankName" className="block text-sm font-medium text-slate-700 mb-2">
                        Bank Name
                      </label>
                      <input
                        type="text"
                        id="bankName"
                        name="bankName"
                        placeholder="Enter bank name"
                        value={formData.bankName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-slate-700 mb-2">
                        Bank Account Number
                      </label>
                      <input
                        type="text"
                        id="bankAccountNumber"
                        name="bankAccountNumber"
                        placeholder="Enter bank account number"
                        value={formData.bankAccountNumber}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>
                  </>
                )}

                {formData.role === "student" && (
                  <>
                    <div>
                      <label htmlFor="dateOfBirth" className="block text-sm font-medium text-slate-700 mb-2">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700"
                      />
                      <p className="text-xs text-slate-400 mt-2">
                        {calculatedStudentAge === null
                          ? "Select the learner's date of birth to store it for report cards and age-based records."
                          : `Calculated age: ${calculatedStudentAge} year${calculatedStudentAge === 1 ? "" : "s"}.`}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="classId" className="block text-sm font-medium text-slate-700 mb-2">
                        Class Assignment
                      </label>
                      <select
                        id="classId"
                        name="classId"
                        value={formData.classId}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 bg-white cursor-pointer"
                      >
                        <option value="">No class assigned yet</option>
                        {availableClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-400 mt-2">
                        {editingUser
                          ? "Update the learner's class assignment here."
                          : "Select the class this learner should join immediately after the account is created."}
                      </p>
                    </div>
                  </>
                )}

                {formData.role === "teacher" && (
                  <>
                    <div>
                      <label htmlFor="teacherClassId" className="block text-sm font-medium text-slate-700 mb-2">
                        Teaching Class
                      </label>
                      <select
                        id="teacherClassId"
                        name="teacherClassId"
                        value={formData.teacherClassId}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 bg-white cursor-pointer"
                      >
                        <option value="">No class assigned yet</option>
                        {availableClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-400 mt-2">
                        {editingUser
                          ? "Update the class this teacher is attached to."
                          : "Pick the class this teacher should be attached to immediately after the account is created."}
                      </p>
                    </div>

                    {formData.teacherClassId && (
                      <div>
                        <label htmlFor="teacherAssignmentRole" className="block text-sm font-medium text-slate-700 mb-2">
                          Class Role
                        </label>
                        <select
                          id="teacherAssignmentRole"
                          name="teacherAssignmentRole"
                          value={formData.teacherAssignmentRole}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 bg-white cursor-pointer"
                        >
                          <option value="class_teacher">Class Teacher</option>
                          <option value="assistant_teacher">Assistant Teacher</option>
                        </select>
                        <p className="text-xs text-slate-400 mt-2">
                          Choose whether this teacher is the main class teacher or the assistant for the selected class.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {formData.role === "student" && (
                  <div>
                    <label htmlFor="transportRouteId" className="block text-sm font-medium text-slate-700 mb-2">
                      Transport Route
                    </label>
                    <select
                      id="transportRouteId"
                      name="transportRouteId"
                      value={formData.transportRouteId}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 bg-white cursor-pointer"
                    >
                      <option value="">Personal (No school transport)</option>
                      {transportRoutes.map((route) => (
                        <option key={route.id} value={route.id}>
                          {route.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400 mt-2">
                      Select a school transport route or keep personal if the learner does not use school transport.
                    </p>
                  </div>
                )}

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    minLength={6}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                  />
                  {editingUser && (
                    <p className="mt-2 text-xs text-slate-400">Only enter a password if you want to change it.</p>
                  )}
                </div>

                {(formData.role === "teacher" || formData.role === "headteacher" || formData.role === "admin") && (
                  <>
                    <div>
                      <label htmlFor="baseSalary" className="block text-sm font-medium text-slate-700 mb-2">
                        Base Salary
                      </label>
                      <input
                        type="number"
                        id="baseSalary"
                        name="baseSalary"
                        placeholder="0.00"
                        value={formData.baseSalary}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="housingAllowance" className="block text-sm font-medium text-slate-700 mb-2">
                          Housing Allowance
                        </label>
                        <input
                          type="number"
                          id="housingAllowance"
                          name="housingAllowance"
                          placeholder="0.00"
                          value={formData.housingAllowance}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="transportAllowance" className="block text-sm font-medium text-slate-700 mb-2">
                          Transport Allowance
                        </label>
                        <input
                          type="number"
                          id="transportAllowance"
                          name="transportAllowance"
                          placeholder="0.00"
                          value={formData.transportAllowance}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="responsibilityAllowance" className="block text-sm font-medium text-slate-700 mb-2">
                          Responsibility Allowance
                        </label>
                        <input
                          type="number"
                          id="responsibilityAllowance"
                          name="responsibilityAllowance"
                          placeholder="0.00"
                          value={formData.responsibilityAllowance}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="otherAllowances" className="block text-sm font-medium text-slate-700 mb-2">
                          Other Allowances
                        </label>
                        <input
                          type="number"
                          id="otherAllowances"
                          name="otherAllowances"
                          placeholder="0.00"
                          value={formData.otherAllowances}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-orange-600 text-white px-5 py-3 rounded-xl font-medium text-sm hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Adding User...
                    </>
                  ) : (
                    <>
                      {editingUser ? <Pencil size={18} /> : <UserPlus size={18} />}
                      {editingUser ? "Save Changes" : "Add User"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Users List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Users className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Users List</h2>
                    <p className="text-sm text-slate-500">
                      {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                    </p>
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none text-slate-700 placeholder:text-slate-400"
                />
              </div>

              {/* Table */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-orange-600" size={32} />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-500 font-medium">
                    {searchTerm ? "No users found matching your search" : "No users yet"}
                  </p>
                  <p className="text-slate-400 text-sm mt-1">
                    {searchTerm ? "Try a different search term" : "Add your first user using the form"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {(() => {
                    const hasHeadteacher = users.some((u) => normalizeRole(u.role) === "headteacher");
                    return (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Name</th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Email</th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Phone</th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Role</th>
                        <th className="text-right py-4 px-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers
                        .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
                        .map((user) => {
                          const roleKey = inferRoleKey(user.role, hasHeadteacher);
                          return (
                            <tr
                            key={user.id}
                            className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-medium">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-medium text-slate-800">{user.name}</span>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-slate-600">{user.email}</td>
                            <td className="py-4 px-4 text-slate-600">{(user as any).phone || "N/A"}</td>
                            <td className="py-4 px-4">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(roleKey)}`}>
                                {formatRoleLabel(roleKey)}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="p-2 bg-slate-100 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors"
                                  title="Edit user"
                                >
                                  <Pencil className="text-slate-600 hover:text-blue-600" size={16} />
                                </button>
                                {normalizeRole(user.role) === "admin" ? null : (
                                  deleteConfirm === user.id ? (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="p-2 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                                        title="Confirm delete"
                                      >
                                        <CheckCircle className="text-red-600" size={16} />
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        title="Cancel"
                                      >
                                        <XCircle className="text-gray-600" size={16} />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setDeleteConfirm(user.id)}
                                      className="p-2 bg-slate-100 rounded-lg hover:bg-red-50 hover:border-red-200 border border-transparent transition-colors"
                                      title="Delete user"
                                    >
                                      <Trash2 className="text-slate-600 hover:text-red-600" size={16} />
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                    </tbody>
                  </table>
                    );
                  })()}

                  {/* Pagination */}
                  {filteredUsers.length > usersPerPage && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-500 font-medium">
                        Showing{" "}
                        <span className="font-bold text-slate-700">
                          {(currentPage - 1) * usersPerPage + 1}–{Math.min(currentPage * usersPerPage, filteredUsers.length)}
                        </span>{" "}
                        of <span className="font-bold text-slate-700">{filteredUsers.length}</span> users
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                          Prev
                        </button>

                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.ceil(filteredUsers.length / usersPerPage) }, (_, i) => i + 1)
                            .filter(page => page === 1 || page === Math.ceil(filteredUsers.length / usersPerPage) || Math.abs(page - currentPage) <= 1)
                            .reduce((acc: (number | string)[], page, idx, arr) => {
                              if (idx > 0 && typeof arr[idx - 1] === 'number' && (page as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                              acc.push(page);
                              return acc;
                            }, [])
                            .map((item, idx) =>
                              item === '...' ? (
                                <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-slate-400">…</span>
                              ) : (
                                <button
                                  key={item}
                                  onClick={() => setCurrentPage(item as number)}
                                  className={`w-9 h-9 text-sm font-bold rounded-lg border transition-all ${currentPage === item
                                    ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-100'
                                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                  {item}
                                </button>
                              )
                            )}
                        </div>

                        <button
                          onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredUsers.length / usersPerPage), p + 1))}
                          disabled={currentPage === Math.ceil(filteredUsers.length / usersPerPage)}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          Next
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
